import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Search, CreditCard, ExternalLink, RefreshCw } from "lucide-react";

/**
 * Admin → Customers list. Read-only view of everyone who has a Stripe customer
 * record on file (created by the webhook on first successful payment).
 *
 * Each row links straight to the Stripe Dashboard for that customer so Sophie
 * can dig into invoices, refunds, or open a Customer Portal from there.
 */
export default function AdminCustomers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  // Load on mount; `refresh()` is the imperative version used by the Refresh button.
  // We deliberately inline the async body inside the effect so eslint can see the
  // state writes happen within an async function, not directly in the effect body.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const r = await api.get("/admin/customers");
        if (!cancelled) setItems(r.data || []);
      } catch (e) {
        // leave items as-is on transient errors
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/customers");
      setItems(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  const filtered = items.filter((c) =>
    !q ||
    (c.email || "").toLowerCase().includes(q.toLowerCase()) ||
    (c.business_name || "").toLowerCase().includes(q.toLowerCase()) ||
    (c.plan_id || "").toLowerCase().includes(q.toLowerCase()) ||
    (c.subscription_status || "").toLowerCase().includes(q.toLowerCase())
  );

  const statusColor = (s) => {
    if (!s) return "bg-[#9ac7f7]/20 text-[#447694]";
    if (s === "active" || s === "trialing") return "bg-emerald-100 text-emerald-700";
    if (s === "past_due") return "bg-amber-100 text-amber-700";
    if (s === "canceled" || s === "incomplete_expired") return "bg-red-100 text-red-700";
    return "bg-[#9ac7f7]/20 text-[#447694]";
  };

  const activeCount = items.filter((c) => c.subscription_status === "active").length;
  const monthlyRevenue = items
    .filter((c) => c.subscription_status === "active")
    .reduce((sum, c) => sum + (c.plan_id === "annual" ? 12.95 : 15), 0)
    .toFixed(2);

  return (
    <div className="p-6 md:p-10" data-testid="admin-customers-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className="bnp-chip bnp-chip-light">Customers</span>
          <h1 className="font-display text-3xl md:text-4xl text-[#04273d] mt-2 font-semibold tracking-tight">Paying customers</h1>
          <p className="text-sm text-[#447694] mt-1">
            {items.length} total &middot; {activeCount} active &middot; ~&pound;{monthlyRevenue} MRR
          </p>
        </div>
        <button
          onClick={refresh}
          className="btn-secondary text-sm"
          data-testid="admin-customers-refresh-button"
          disabled={loading}
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="mt-6 max-w-md">
        <div className="relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#447694]" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search email, business, plan, status"
            className="w-full pl-11 pr-4 py-3 rounded-full border-2 border-[#9ac7f7]/40 focus:border-[#04273d] focus:outline-none font-display text-[#04273d] text-sm"
            data-testid="admin-customers-search"
          />
        </div>
      </div>

      <div className="mt-6 bnp-card overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-sm text-[#447694]" data-testid="admin-customers-loading">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center" data-testid="admin-customers-empty">
            <CreditCard size={32} className="text-[#9ac7f7] mx-auto" />
            <p className="font-display font-semibold text-[#04273d] mt-3">No paying customers yet</p>
            <p className="text-sm text-[#447694] mt-1">Customers appear here automatically once a Stripe checkout completes.</p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="admin-customers-table">
            <thead className="bg-[#f7f7f7] border-b border-[#9ac7f7]/20">
              <tr className="text-left">
                <th className="px-5 py-3 font-display font-semibold text-[#04273d]">Email</th>
                <th className="px-5 py-3 font-display font-semibold text-[#04273d]">Business</th>
                <th className="px-5 py-3 font-display font-semibold text-[#04273d]">Plan</th>
                <th className="px-5 py-3 font-display font-semibold text-[#04273d]">Status</th>
                <th className="px-5 py-3 font-display font-semibold text-[#04273d]">Last invoice</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-t border-[#9ac7f7]/15 hover:bg-[#f7f7f7]/60" data-testid={`admin-customer-row-${c.email}`}>
                  <td className="px-5 py-3 text-[#04273d] font-medium">{c.email || "-"}</td>
                  <td className="px-5 py-3 text-[#447694]">{c.business_name || "-"}</td>
                  <td className="px-5 py-3 text-[#447694] capitalize">{c.plan_id || "-"}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-display font-semibold capitalize ${statusColor(c.subscription_status)}`}>
                      {c.subscription_status ? c.subscription_status.replaceAll("_", " ") : "unknown"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-[#447694]">
                    {c.last_invoice_amount ? `£${Number(c.last_invoice_amount).toFixed(2)}` : "-"}
                    {c.last_invoice_paid_at && (
                      <span className="block text-[11px] text-[#9ac7f7]">{new Date(c.last_invoice_paid_at).toLocaleDateString("en-GB")}</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right">
                    {c.stripe_customer_id && (
                      <a
                        href={`https://dashboard.stripe.com/customers/${c.stripe_customer_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[11.5px] text-[#0d9b80] hover:underline"
                        data-testid={`admin-customer-stripe-link-${c.email}`}
                      >
                        Stripe <ExternalLink size={11} />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
